import { RefObject, useEffect, useRef } from 'react'
import { handleFocusNavigation } from '../../../components/Modal/utils'

export interface UseCommunityModalOptions {
  /** Ref to the dialog/modal container element for focus trapping */
  dialogRef: RefObject<HTMLElement | null>
  /** Called when the user presses Escape */
  onClose: () => void
  /** Whether the modal is currently open (controls scroll lock + listeners) */
  enabled?: boolean
}

/**
 * Shared modal behavior for Community modals.
 *
 * Composes three concerns that every Community modal needs:
 * 1. **Scroll lock** — adds `body__modal--open` CSS class (same as FollowersModal)
 * 2. **Keyboard handling** — Escape closes, Tab cycles within the dialog
 * 3. **Focus management** — auto-focuses dialog on mount, restores previous
 *    focus on unmount
 *
 * Extracted from CommunityProfileFollowersModal (lines 62-90) which already
 * implements all three correctly. PlaylistSharePreview and VibeQuizModal
 * should adopt this hook instead of their partial implementations.
 *
 * @example
 * ```tsx
 * const dialogRef = useRef<HTMLDivElement>(null)
 * useCommunityModal({ dialogRef, onClose: handleClose })
 *
 * return (
 *   <div ref={dialogRef} role="dialog" aria-modal="true" tabIndex={-1}>
 *     ...
 *   </div>
 * )
 * ```
 */
export function useCommunityModal ({
  dialogRef,
  onClose,
  enabled = true,
}: UseCommunityModalOptions): void {
  const previousActiveElementRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!enabled) return undefined

    // Save the element that had focus before the modal opened
    previousActiveElementRef.current = document.activeElement

    // Scroll lock via CSS class (consistent with FollowersModal)
    document.body.classList.add('body__modal--open')

    // Auto-focus the dialog container
    if (dialogRef.current) {
      dialogRef.current.focus()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      // Focus trap: Tab cycles within the dialog
      if (dialogRef.current) {
        handleFocusNavigation(e, dialogRef.current)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('body__modal--open')

      // Restore focus to the element that triggered the modal
      if (previousActiveElementRef.current instanceof HTMLElement) {
        previousActiveElementRef.current.focus()
      }
    }
  }, [dialogRef, onClose, enabled])
}
